import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Session, TestPlan, Feature, Component } from '../../types';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useProduct } from '../../contexts/ProductContext';
import { apiRequest } from '../../utils/api';
import { useToast, checkPendingMessage, showTempMessage } from '../../utils/messages';
import { Toast } from '../../utils/messages';
import SessionCard from './SessionCard';
import SessionDetails from './SessionDetails';
import CreateSessionModal from './CreateSessionModal';
import TestPlanDetails from '../testplan/TestPlanDetails';
import SearchFilterBar from '../shared/SearchFilterBar';

const Sessions: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentWorkspace } = useWorkspace();
  const { selectedProduct } = useProduct();
  const userRole = currentWorkspace?.role || 'user';
  const [activeTab, setActiveTab] = useState<'todo' | 'inprogress' | 'completed'>('todo');
  const [todoSessions, setTodoSessions] = useState<Session[]>([]);
  const [inProgressSessions, setInProgressSessions] = useState<Session[]>([]);
  const [completedSessions, setCompletedSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const detailsRef = useRef<HTMLDivElement>(null);
  const previousProductIdRef = useRef<string | undefined>(selectedProduct?.id);
  const [sortField, setSortField] = useState<'id' | 'deadline' | 'createdAt' | 'user' | 'sessionType' | 'milestone'>('id');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [filterText, setFilterText] = useState('');
  const [filterMilestone, setFilterMilestone] = useState('');
  const [filterCompletedFrom, setFilterCompletedFrom] = useState('');
  const [filterCompletedTo, setFilterCompletedTo] = useState('');
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editingSession, setEditingSession] = useState<Session | null>(null);
  const [selectedTestPlan, setSelectedTestPlan] = useState<TestPlan | null>(null);
  const [testPlanFeatures, setTestPlanFeatures] = useState<Feature[]>([]);
  const [testPlanComponents, setTestPlanComponents] = useState<Component[]>([]);
  const [milestones, setMilestones] = useState<Record<string, { startDate: string; endDate?: string }>>({});
  const [testPlanMap, setTestPlanMap] = useState<Map<string, string>>(new Map()); // Map testPlanUrl to test plan title
  const { message, setMessage } = useToast();

  // Fetch session data when editing (refetch every time modal opens)
  useEffect(() => {
    if (editingSessionId) {
      const fetchSession = async () => {
        try {
          // Always fetch fresh data when opening edit modal
          const session = await apiRequest<Session>(`/api/sessions/${editingSessionId}`, {}, { useAuth: true });
          setEditingSession(session);
        } catch (error) {
          console.error('Error fetching session for editing:', error);
          setEditingSessionId(null);
          setEditingSession(null);
        }
      };
      fetchSession();
    } else {
      setEditingSession(null);
    }
  }, [editingSessionId]);

  // Check for pending messages from navigation (e.g., after creating a session)
  useEffect(() => {
    checkPendingMessage(setMessage);
  }, [setMessage]);

  // Fetch milestones when product changes
  useEffect(() => {
    const productId = selectedProduct?.id;
    if (!productId) {
      setMilestones({});
      return;
    }
    const fetchMilestones = async () => {
      try {
        const apiUrl = `/api/milestones?productId=${encodeURIComponent(productId)}`;
        const data = await apiRequest<Array<{ name: string; startDate: string; endDate?: string }>>(
          apiUrl,
          {},
          { useAuth: true }
        );
        const milestoneMap: Record<string, { startDate: string; endDate?: string }> = {};
        (Array.isArray(data) ? data : []).forEach(m => {
          milestoneMap[m.name] = { startDate: m.startDate, endDate: m.endDate };
        });
        setMilestones(milestoneMap);
      } catch (error) {
        console.error('Error fetching milestones:', error);
        setMilestones({});
      }
    };
    fetchMilestones();
  }, [selectedProduct?.id]);

  const fetchSessions = useCallback(async () => {
    try {
      // Fetch all sessions first, then filter by product on client side
      const [todoData, inProgressData, completedData] = await Promise.all([
        apiRequest<Session[]>('/api/sessions?status=todo', {}, { useAuth: true, setMessage }),
        apiRequest<Session[]>('/api/sessions?status=inprogress', {}, { useAuth: true, setMessage }),
        apiRequest<Session[]>('/api/sessions?status=completed', {}, { useAuth: true, setMessage })
      ]);
      
      // Collect all unique test plan IDs from sessions (more efficient than fetching all test plans)
      const allSessions = [...(todoData || []), ...(inProgressData || []), ...(completedData || [])];
      const testPlanIds = new Set<string>();
      const testPlanUrlToId = new Map<string, string>();
      
      allSessions.forEach((session: Session) => {
        if (session.testPlanUrl) {
          // Extract test plan ID from URL (e.g., "http://localhost:3000/testplan/123" -> "123")
          const testPlanIdMatch = session.testPlanUrl.match(/\/testplan\/([^/?]+)/);
          const testPlanId = testPlanIdMatch ? testPlanIdMatch[1] : null;
          if (testPlanId) {
            testPlanIds.add(testPlanId);
            testPlanUrlToId.set(session.testPlanUrl, testPlanId);
          }
        }
      });

      // Fetch only the test plans that are actually referenced by sessions
      const testPlanMap = new Map<string, string>();
      if (testPlanIds.size > 0) {
        try {
          // Use the new by-ids endpoint to fetch only needed test plans
          const testPlanIdsArray = Array.from(testPlanIds);
          const testPlans = await apiRequest<TestPlan[]>(
            `/api/testplans/by-ids?ids=${testPlanIdsArray.join(',')}`, 
            {}, 
            { useAuth: true, setMessage: undefined }
          );
          
          // Build the map from URL to title
          testPlanUrlToId.forEach((testPlanId, url) => {
            const testPlan = testPlans?.find((tp: TestPlan) => tp.id === testPlanId);
            if (testPlan) {
              testPlanMap.set(url, testPlan.title);
            }
          });
        } catch (error) {
          console.error('Error fetching test plans for search:', error);
        }
      }
      setTestPlanMap(testPlanMap);
      
      // Filter by selected product if one is selected
      // Only filter if we have a valid selectedProduct (not just a temporary undefined state)
      const filterByProduct = (sessions: Session[]) => {
        if (!selectedProduct?.id) {
          // If product is temporarily undefined, return all sessions
          // The state update logic below will prevent clearing existing filtered sessions
          return sessions || [];
        }
        return (sessions || []).filter((s: Session) => s.productId === selectedProduct.id);
      };
      
      const filteredTodo = filterByProduct(todoData);
      const filteredInProgress = filterByProduct(inProgressData);
      const filteredCompleted = filterByProduct(completedData);
      
      console.log('[Workbook] Fetched sessions:', {
        productId: selectedProduct?.id,
        todo: filteredTodo.length,
        inProgress: filteredInProgress.length,
        completed: filteredCompleted.length,
        completedIds: filteredCompleted.map((s: Session) => s.id)
      });
      
      // Always update state - if product is undefined, we'll show all sessions
      // This is fine because the user should always have a product selected
      // Only update state if we have a valid product selected
      // This prevents clearing sessions when product is temporarily undefined (e.g., when modal opens)
      if (selectedProduct?.id) {
        setTodoSessions(filteredTodo);
        setInProgressSessions(filteredInProgress);
        setCompletedSessions(filteredCompleted);
        previousProductIdRef.current = selectedProduct.id;
      } else if (!previousProductIdRef.current) {
        // Only update if we never had a product selected (initial load)
        setTodoSessions(filteredTodo);
        setInProgressSessions(filteredInProgress);
        setCompletedSessions(filteredCompleted);
      }
      // If product becomes undefined but we had one before, keep existing sessions
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setLoading(false);
    }
  }, [setMessage, selectedProduct?.id]);

  // Clear selected session when product changes
  useEffect(() => {
    // If we have a selected session, check if it belongs to the current product
    if (selectedSession && selectedProduct?.id) {
      if (selectedSession.productId !== selectedProduct.id) {
        // Session belongs to a different product, clear it
        setSelectedSession(null);
      }
    } else if (selectedSession && !selectedProduct?.id) {
      // Product was cleared, clear selected session
      setSelectedSession(null);
    }
  }, [selectedProduct?.id, selectedSession]);

  useEffect(() => {
    // Check if we're coming from session submission
    const state = location.state as { selectedSessionId?: string; activeTab?: 'todo' | 'inprogress' | 'completed' } | null;
    if (state?.activeTab) {
      setActiveTab(state.activeTab);
    }
    // Always fetch sessions when location changes
    fetchSessions();
  }, [location.pathname, navigate, location.state, fetchSessions]);

  // Handle session selection from navigation state
  // Use a ref to track if we've already processed this navigation state
  const processedNavigationStateRef = useRef<string | null>(null);
  
  useEffect(() => {
    const state = location.state as { selectedSessionId?: string; activeTab?: 'todo' | 'inprogress' | 'completed' } | null;
    if (state?.selectedSessionId) {
      // Create a unique key for this navigation state
      const stateKey = `${state.selectedSessionId}-${location.pathname}`;
      
      // Only process if we haven't already processed this navigation state
      if (processedNavigationStateRef.current !== stateKey) {
        processedNavigationStateRef.current = stateKey;
        console.log('[Workbook] Navigation state received:', state);
        // Fetch the session fresh from the API to get the current status
        const fetchAndSelectSession = async () => {
          try {
            // Fetch the specific session (fetchSessions already called on location change)
            console.log('[Workbook] Fetching session:', state.selectedSessionId);
            const session = await apiRequest<Session>(`/api/sessions/${state.selectedSessionId}`);
            console.log('[Workbook] Session fetched:', session.id, 'status:', session.status);
            
            // Only select the session if it belongs to the current product
            if (session.productId === selectedProduct?.id) {
              setSelectedSession(session);
              
              // Switch to the correct tab based on actual status
              const tabToSet = state.activeTab || (session.status === 'completed' ? 'completed' : session.status === 'inprogress' ? 'inprogress' : 'todo');
              console.log('[Workbook] Setting active tab to:', tabToSet);
              setActiveTab(tabToSet);
            } else {
              // Session doesn't belong to current product, clear the navigation state
              processedNavigationStateRef.current = null;
            }
            
            // Clear the state after a short delay to ensure UI updates
            setTimeout(() => {
              window.history.replaceState({}, document.title);
            }, 200);
          } catch (error) {
            console.error('Error fetching session:', error);
            processedNavigationStateRef.current = null;
          }
        };
        
        fetchAndSelectSession();
      }
    } else {
      // No navigation state, reset the ref
      processedNavigationStateRef.current = null;
    }
  }, [location.state, location.pathname, fetchSessions, selectedProduct?.id]);


  const handleSelectSession = async (session: Session | null) => {
    if (!session) {
      setSelectedSession(null);
      return;
    }
    
    try {
      // Fetch fresh session data
      const freshSession = await apiRequest<Session>(`/api/sessions/${session.id}`);
      setSelectedSession(freshSession);
      
      // Switch to correct tab based on status
      if (freshSession.status === 'completed') {
        setActiveTab('completed');
      } else if (freshSession.status === 'inprogress') {
        setActiveTab('inprogress');
      } else {
        setActiveTab('todo');
      }
      await fetchSessions();
    } catch (error) {
      console.error('Error fetching session:', error);
      // Fallback to using the session from props
      setSelectedSession(session);
    }
  };

  const handleOpenTestPlan = async (testPlanUrl: string) => {
    // Extract test plan ID from URL
    const testPlanIdMatch = testPlanUrl.match(/\/testplan\/([^/?]+)/);
    const testPlanId = testPlanIdMatch ? testPlanIdMatch[1] : null;
    
    if (!testPlanId) return;
    
    try {
      // Fetch test plan and related data
      const [testPlanData, featuresData, componentsData] = await Promise.all([
        apiRequest<TestPlan>(`/api/testplans/${testPlanId}`, {}, { useAuth: true }),
        apiRequest<Feature[]>('/api/features', {}, { useAuth: true }),
        apiRequest<Component[]>('/api/components', {}, { useAuth: true })
      ]);
      
      setSelectedTestPlan(testPlanData);
      setTestPlanFeatures(Array.isArray(featuresData) ? featuresData : []);
      setTestPlanComponents(Array.isArray(componentsData) ? componentsData : []);
    } catch (error) {
      console.error('Error fetching test plan:', error);
      showTempMessage(setMessage, { type: 'error', text: 'Failed to load test plan' });
    }
  };

  const sortSessions = (sessions: Session[]): Session[] => {
    const sorted = [...sessions].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'id':
          // Smart ID sorting: handle formats like "FS-1", "FS-2", "FS-10"
          // Extract prefix and number for proper numeric sorting
          const aIdMatch = a.id.match(/^([A-Z]+-)?(\d+)$/);
          const bIdMatch = b.id.match(/^([A-Z]+-)?(\d+)$/);
          
          if (aIdMatch && bIdMatch) {
            // Both have numeric IDs
            const aPrefix = aIdMatch[1] || '';
            const bPrefix = bIdMatch[1] || '';
            const aNum = parseInt(aIdMatch[2], 10);
            const bNum = parseInt(bIdMatch[2], 10);
            
            // First compare prefixes
            if (aPrefix !== bPrefix) {
              aValue = aPrefix;
              bValue = bPrefix;
            } else {
              // Same prefix, compare numbers
              aValue = aNum;
              bValue = bNum;
            }
          } else {
            // Fallback to string comparison
            aValue = a.id;
            bValue = b.id;
          }
          break;
        case 'deadline':
          aValue = a.deadline || '';
          bValue = b.deadline || '';
          break;
        case 'createdAt':
          aValue = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          bValue = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          break;
        case 'user':
          aValue = a.user;
          bValue = b.user;
          break;
        case 'sessionType':
          aValue = a.sessionType;
          bValue = b.sessionType;
          break;
        case 'milestone':
          aValue = a.milestone || '';
          bValue = b.milestone || '';
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  const filterSessions = (sessions: Session[]): Session[] => {
    let filtered = sessions;

    // Filter by milestone if selected
    if (filterMilestone) {
      filtered = filtered.filter(session => 
        session.milestone && session.milestone.toLowerCase() === filterMilestone.toLowerCase()
      );
    }

    // Filter by completed date range
    if (filterCompletedFrom || filterCompletedTo) {
      filtered = filtered.filter(session => {
        if (!session.completedAt) return false;
        const completedDate = new Date(session.completedAt);
        completedDate.setHours(0, 0, 0, 0); // Normalize to start of day
        
        if (filterCompletedFrom) {
          const fromDate = new Date(filterCompletedFrom);
          fromDate.setHours(0, 0, 0, 0);
          if (completedDate < fromDate) return false;
        }
        
        if (filterCompletedTo) {
          const toDate = new Date(filterCompletedTo);
          toDate.setHours(23, 59, 59, 999); // End of day
          if (completedDate > toDate) return false;
        }
        
        return true;
      });
    }

    // Filter by text search
    if (filterText.trim()) {
      const filter = filterText.toLowerCase();
      filtered = filtered.filter(session => {
        // Get test plan title if session has a test plan URL
        const testPlanTitle = session.testPlanUrl ? testPlanMap.get(session.testPlanUrl) : null;
        
        return (
          session.id.toLowerCase().includes(filter) ||
          session.user.toLowerCase().includes(filter) ||
          session.sessionType.toLowerCase().includes(filter) ||
          (session.platforms && session.platforms.some(p => p.toLowerCase().includes(filter))) ||
          (session.platform && session.platform.toLowerCase().includes(filter)) ||
          session.milestone.toLowerCase().includes(filter) ||
          (session.notes && session.notes.toLowerCase().includes(filter)) ||
          (session.referenceUrl && session.referenceUrl.toLowerCase().includes(filter)) ||
          (testPlanTitle && testPlanTitle.toLowerCase().includes(filter))
        );
      });
    }

    return filtered;
  };

  const getDisplayedSessions = (sessions: Session[]): Session[] => {
    const filtered = filterSessions(sessions);
    return sortSessions(filtered);
  };

  // Get available milestones from current tab's sessions
  const getAvailableMilestones = (): string[] => {
    const currentSessions = activeTab === 'todo' ? todoSessions 
      : activeTab === 'inprogress' ? inProgressSessions 
      : completedSessions;
    const milestones = new Set<string>();
    currentSessions.forEach(session => {
      if (session.milestone && session.milestone.trim()) {
        milestones.add(session.milestone);
      }
    });
    return Array.from(milestones).sort();
  };

  if (loading) {
    return <div className="form-container">Loading...</div>;
  }

  return (
    <div className="form-container">
      <Toast message={message} />
      <div className="page-header">
        <h1 className="page-title">Sessions</h1>
        <button
          className="btn btn-primary"
          onClick={() => navigate('/sessions/new')}
        >
          Create New Session
        </button>
      </div>

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'todo' ? 'active' : ''}`}
          onClick={() => setActiveTab('todo')}
        >
          To Do ({todoSessions.length})
        </button>
        <button
          className={`tab ${activeTab === 'inprogress' ? 'active' : ''}`}
          onClick={() => setActiveTab('inprogress')}
        >
          In Progress ({inProgressSessions.length})
        </button>
        <button
          className={`tab ${activeTab === 'completed' ? 'active' : ''}`}
          onClick={() => setActiveTab('completed')}
        >
          Completed ({completedSessions.length})
        </button>
      </div>

      {/* Sort and Filter Controls */}
      <SearchFilterBar
        context="sessions"
        sortField={sortField}
        sortDirection={sortDirection}
        filterText={filterText}
        filterMilestone={filterMilestone}
        availableMilestones={getAvailableMilestones()}
        milestoneMetadata={milestones}
        onSortFieldChange={(field) => setSortField(field as typeof sortField)}
        onSortDirectionChange={setSortDirection}
        onFilterTextChange={setFilterText}
        onFilterMilestoneChange={setFilterMilestone}
        showCompletedDateFilter={activeTab === 'completed'}
        filterCompletedFrom={filterCompletedFrom}
        filterCompletedTo={filterCompletedTo}
        onFilterCompletedFromChange={setFilterCompletedFrom}
        onFilterCompletedToChange={setFilterCompletedTo}
      />

      <div className="session-grid">
        {activeTab === 'todo' ? (
          (() => {
            const displayed = getDisplayedSessions(todoSessions);
            return displayed.length === 0 ? (
              <div className="card">
                <p>{filterText ? 'No sessions match your filter.' : 'No sessions in To Do. Create a new session to get started!'}</p>
              </div>
            ) : (
              displayed.map((session) => (
                <SessionCard
                  key={session.id}
                  session={session}
                  isSelected={selectedSession?.id === session.id}
                  onSelect={handleSelectSession}
                  onSessionChange={fetchSessions}
                  setMessage={setMessage}
                  onEdit={setEditingSessionId}
                  onOpenTestPlan={handleOpenTestPlan}
                  milestones={milestones}
                />
              ))
            );
          })()
        ) : activeTab === 'inprogress' ? (
          (() => {
            const displayed = getDisplayedSessions(inProgressSessions);
            return displayed.length === 0 ? (
              <div className="card">
                <p>{filterText ? 'No sessions match your filter.' : 'No sessions in progress.'}</p>
              </div>
            ) : (
              displayed.map((session) => (
                <SessionCard
                  key={session.id}
                  session={session}
                  isSelected={selectedSession?.id === session.id}
                  onSelect={handleSelectSession}
                  onSessionChange={fetchSessions}
                  setMessage={setMessage}
                  onEdit={setEditingSessionId}
                  onOpenTestPlan={handleOpenTestPlan}
                  milestones={milestones}
                />
              ))
            );
          })()
        ) : (
          (() => {
            const displayed = getDisplayedSessions(completedSessions);
            return displayed.length === 0 ? (
              <div className="card">
                <p>{filterText ? 'No sessions match your filter.' : 'No completed sessions.'}</p>
              </div>
            ) : (
              displayed.map((session) => (
                <SessionCard
                  key={session.id}
                  session={session}
                  isSelected={selectedSession?.id === session.id}
                  onSelect={handleSelectSession}
                  onSessionChange={fetchSessions}
                  setMessage={setMessage}
                  onEdit={setEditingSessionId}
                  onOpenTestPlan={handleOpenTestPlan}
                  milestones={milestones}
                />
              ))
            );
          })()
        )}

        {/* Session Details Modal */}
        {selectedSession && (
          <SessionDetails
            session={selectedSession}
            onClose={() => setSelectedSession(null)}
            userRole={userRole}
            detailsRef={detailsRef}
            milestones={milestones}
            onSessionChange={fetchSessions}
            onEdit={setEditingSessionId}
          />
        )}

        {/* Create Session - Now a separate page at /sessions/new */}

        {/* Edit Session Modal */}
        {editingSessionId && (
          <CreateSessionModal
            isOpen={!!editingSessionId}
            onClose={() => {
              setEditingSessionId(null);
              setEditingSession(null);
            }}
            session={editingSession}
            onSessionCreated={async (updatedSession: Session) => {
              await fetchSessions();
              // Update selected session if it's the one being edited
              if (selectedSession?.id === updatedSession.id) {
                setSelectedSession(updatedSession);
              }
              // Refetch the session data to ensure we have the latest when reopening
              const freshSession = await apiRequest<Session>(`/api/sessions/${updatedSession.id}`, {}, { useAuth: true });
              setEditingSession(freshSession);
            }}
          />
        )}

        {/* Test Plan Details Modal */}
        {selectedTestPlan && (
          <TestPlanDetails
            scope={selectedTestPlan}
            onClose={() => setSelectedTestPlan(null)}
            features={testPlanFeatures}
            components={testPlanComponents}
            onEdit={() => {
              // Don't allow editing from sessions page - just close modal
              setSelectedTestPlan(null);
            }}
            onDelete={() => {
              // Don't allow deleting from sessions page - just close modal
              setSelectedTestPlan(null);
            }}
            zIndex={2000}
          />
        )}
      </div>
    </div>
  );
};

export default Sessions;

